import React, { useMemo } from 'react'
import Select from 'react-select'
import { groupColumnsForDisplay } from '../dashboard/utils'

/**
 * Groups columns by their `grouping` property the same way the dashboard
 * column selector modal does: "ThirdLine" forced first, "Calculated" forced
 * last, the rest alphabetically. Columns inside each group follow the shared
 * `compareColumnsForDisplay` order (account columns in business order, the rest
 * alphabetically by display name).
 */
const buildGroupedOptions = (columns, getColumnDisplayName, municipality) => {
  if (!columns || columns.length === 0) return []

  return groupColumnsForDisplay(columns, municipality).map(({ label, columns: grouped }) => ({
    label,
    options: grouped.map((c) => ({
      value: c.id,
      label: getColumnDisplayName(c, municipality),
      column: c
    }))
  }))
}

// Shared by every small react-select in the chart modal so they match the
// bootstrap form controls around them.
export const smSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 31,
    fontSize: '0.875rem',
    borderColor: state.selectProps.isInvalid
      ? '#dc3545'
      : state.isFocused
      ? '#86b7fe'
      : '#ced4da',
    boxShadow: state.isFocused
      ? state.selectProps.isInvalid
        ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
        : '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
      : 'none',
    '&:hover': { borderColor: state.selectProps.isInvalid ? '#dc3545' : '#86b7fe' }
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 29 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  groupHeading: (base) => ({
    ...base,
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: '#6c757d'
  }),
  option: (base) => ({ ...base, fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, zIndex: 5 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
}

// For a menu portaled out of the chart modal. An absolute menu is positioned off the page's
// scroll offset and scrolled into view by moving the page behind the modal, which react-select
// then reads back as a new position — a loop React aborts with "Maximum update depth exceeded".
// Fixed positioning takes the page's scroll out of the calculation.
export const portaledMenuProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  menuShouldScrollIntoView: false
}

/**
 * Searchable, grouped dropdown for selecting a table column. Columns are
 * grouped to match the dashboard column selector modal.
 */
const SearchableColumnSelect = ({
  columns,
  value,
  onChange,
  getColumnDisplayName,
  municipality,
  placeholder = 'Select column...',
  isInvalid = false,
  isClearable = false,
  inputId
}) => {
  const groupedOptions = useMemo(
    () => buildGroupedOptions(columns || [], getColumnDisplayName, municipality),
    [columns, getColumnDisplayName, municipality]
  )

  const flatOptions = useMemo(
    () => groupedOptions.flatMap((g) => g.options),
    [groupedOptions]
  )

  const selectedOption = value
    ? flatOptions.find((o) => String(o.value) === String(value)) || null
    : null

  return (
    <Select
      inputId={inputId}
      options={groupedOptions}
      value={selectedOption}
      onChange={(opt) => onChange(opt ? opt.value : '')}
      placeholder={placeholder}
      isClearable={isClearable}
      isInvalid={isInvalid}
      styles={smSelectStyles}
      {...portaledMenuProps}
    />
  )
}

export default SearchableColumnSelect
